import "./QuestionGroupEditor.less";

import {
    GuxFormFieldCheckbox,
    GuxFormFieldCheckboxGroupBeta,
    GuxTooltip,
    GuxInlineAlert,
} from "genesys-spark-components-react";
import { useStrings } from "features/i18n/useStrings";
import strings from "./QuestionGroupEditor.strings";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { qualityFormActions } from "features/qualityForm/qualityFormSlice";
import { selectFormType } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import FormType from "enums/FormType";
import FeatureToggle from "enums/FeatureToggle";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";

interface DefaultAnswerOptionProps {
    isDisabled?: boolean;
    isChecked?: boolean;
    onToggle: () => void;
    label: string;
}

function DefaultAnswerOption({
    isDisabled = false,
    isChecked,
    onToggle,
    label,
}: DefaultAnswerOptionProps) {
    const t = useStrings(strings);
    return (
        <span>
            <GuxFormFieldCheckbox>
                <input
                    slot="input"
                    type="checkbox"
                    checked={isChecked}
                    disabled={isDisabled}
                    aria-disabled={isDisabled}
                    onChange={onToggle}
                    className="property-section-checkbox"
                />
                <label slot="label">{label}</label>
            </GuxFormFieldCheckbox>
            {isDisabled && (
                <GuxTooltip
                    data-testid={`default-scoring-tooltip-${label
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                >
                    {t.labels.tooltip.disabledDefaultAnswerToOptions}
                </GuxTooltip>
            )}
        </span>
    );
}

interface DefaultAnswerSectionProps {
    questionGroupId: string;
    questionGroup: {
        defaultAnswersTo?: {
            notApplicable?: boolean;
            highestScore?: boolean;
            lowestScore?: boolean;
            userDefined?: boolean;
        };
        defaultAnswersToNA?: boolean;
        defaultAnswersToHighest?: boolean;
    };
}

export function AnswersDefaultToSection({
    questionGroupId,
    questionGroup,
}: DefaultAnswerSectionProps) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();

    const isEvaluationFormDefaultAnsweringEnabled = useAppSelector((state) =>
        selectFeatureToggle(
            state,
            FeatureToggle.EVALUATION_FORM_DEFAULT_ANSWERING
        )
    );

    const isAssessment = useAppSelector(
        (state) => selectFormType(state) === FormType.ASSESSMENT
    );

    const isHighestScoringDisabled = !!(
        isEvaluationFormDefaultAnsweringEnabled &&
        (questionGroup.defaultAnswersTo?.lowestScore ||
            questionGroup.defaultAnswersTo?.userDefined)
    );

    const isLowestScoringDisabled = !!(
        isEvaluationFormDefaultAnsweringEnabled &&
        (questionGroup.defaultAnswersTo?.highestScore ||
            questionGroup.defaultAnswersTo?.userDefined ||
            questionGroup.defaultAnswersTo?.notApplicable)
    );

    const isUserDefinedDisabled = !!(
        isEvaluationFormDefaultAnsweringEnabled &&
        (questionGroup.defaultAnswersTo?.highestScore ||
            questionGroup.defaultAnswersTo?.lowestScore ||
            questionGroup.defaultAnswersTo?.notApplicable)
    );

    const isNotApplicableDisabled =
        isEvaluationFormDefaultAnsweringEnabled &&
        (questionGroup.defaultAnswersTo?.lowestScore ||
            questionGroup.defaultAnswersTo?.userDefined);

    return (
        <>
            <GuxFormFieldCheckboxGroupBeta>
                <div className="property-section-checkboxes">
                    <DefaultAnswerOption
                        isDisabled={isNotApplicableDisabled}
                        isChecked={
                            isEvaluationFormDefaultAnsweringEnabled &&
                            !isAssessment
                                ? questionGroup.defaultAnswersTo?.notApplicable
                                : questionGroup.defaultAnswersToNA
                        }
                        onToggle={() => {
                            dispatch(
                                qualityFormActions.toggleDefaultAnswersToNA({
                                    groupId: questionGroupId,
                                    isEvaluationFormDefaultAnsweringEnabled,
                                })
                            );
                        }}
                        label={t.labels.defaultNA}
                    />
                    {!isAssessment && (
                        <>
                            <DefaultAnswerOption
                                isDisabled={isHighestScoringDisabled ?? false}
                                isChecked={
                                    isEvaluationFormDefaultAnsweringEnabled
                                        ? questionGroup.defaultAnswersTo
                                              ?.highestScore
                                        : questionGroup.defaultAnswersToHighest
                                }
                                onToggle={() => {
                                    dispatch(
                                        qualityFormActions.toggleDefaultAnswersToHighest(
                                            {
                                                groupId: questionGroupId,
                                                isEvaluationFormDefaultAnsweringEnabled,
                                            }
                                        )
                                    );
                                }}
                                label={t.labels.defaultHighestScoring}
                            />

                            {isEvaluationFormDefaultAnsweringEnabled && (
                                <>
                                    <DefaultAnswerOption
                                        isDisabled={
                                            isLowestScoringDisabled ?? false
                                        }
                                        isChecked={
                                            questionGroup.defaultAnswersTo
                                                ?.lowestScore
                                        }
                                        onToggle={() => {
                                            dispatch(
                                                qualityFormActions.toggleDefaultAnswersToLowest(
                                                    {
                                                        groupId:
                                                            questionGroupId,
                                                    }
                                                )
                                            );
                                        }}
                                        label={t.labels.defaultLowestScoring}
                                    />
                                    <DefaultAnswerOption
                                        isDisabled={
                                            isUserDefinedDisabled ?? false
                                        }
                                        isChecked={
                                            questionGroup.defaultAnswersTo
                                                ?.userDefined
                                        }
                                        onToggle={() => {
                                            dispatch(
                                                qualityFormActions.toggleDefaultAnswersToUserDefined(
                                                    {
                                                        groupId:
                                                            questionGroupId,
                                                    }
                                                )
                                            );
                                        }}
                                        label={t.labels.defaultUserDefined}
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>
            </GuxFormFieldCheckboxGroupBeta>
            {isEvaluationFormDefaultAnsweringEnabled && !isAssessment && (
                <GuxInlineAlert accent="info" className="default-answers-info">
                    <div
                        slot="content"
                        data-testid="default-answers-info-message"
                    >
                        {t.labels.infoBanner.defaultAnswersToMessage}
                    </div>
                </GuxInlineAlert>
            )}
        </>
    );
}
